import { useState } from 'react';
import { Card, CardBody, Badge, Button, Input } from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { RelativeTime } from '../../components/RelativeTime';
import { useHasPermission } from '../../rbac/useHasPermission';
import { useResellers, useApproveReseller, useSetResellerStatus } from './useResellers';

const STATUS_COLOR = { pending: 'warning', active: 'success', suspended: 'danger', closed: 'secondary' };
const STATUS_LABEL = { pending: 'offen', active: 'aktiv', suspended: 'gesperrt', closed: 'geschlossen' };
const FILTERS = ['', 'pending', 'active', 'suspended', 'closed'];

// #187 P6 / #228 — Operator-Verwaltung der White-Label-Reseller.
export function ResellersPage() {
  const [status, setStatus] = useState('');
  const query = useResellers({ status });
  const approve = useApproveReseller();
  const setStatusM = useSetResellerStatus();
  const canWrite = useHasPermission({ perms: ['users.write'] });
  const items = query.data?.resellers ?? [];

  const suspend = (r) => {
    const reason = window.prompt(`Reseller „${r.companyName}" sperren — Grund (optional):`, '');
    if (reason === null) return;
    setStatusM.mutate({ id: r.id, status: 'suspended', reason: reason || undefined });
  };
  const reactivate = (r) => setStatusM.mutate({ id: r.id, status: 'active' });

  return (
    <div>
      <PageHeader
        title="Reseller-Verwaltung"
        subtitle="White-Label-Reseller freigeben, sperren und Status verwalten (#187 P6)"
      />
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div style={{ maxWidth: 220 }}>
              <Input type="select" bsSize="sm" value={status} onChange={(e) => setStatus(e.target.value)}>
                {FILTERS.map((f) => <option key={f || 'all'} value={f}>{f ? STATUS_LABEL[f] : 'Alle Status'}</option>)}
              </Input>
            </div>
            <span className="text-muted small">{items.length} Reseller</span>
          </div>

          <DataTable
            loading={query.isLoading}
            error={query.error}
            empty={!query.isLoading && items.length === 0}
            emptyMessage="Keine Reseller in diesem Status."
            onRetry={() => query.refetch()}
          >
            <thead>
              <tr>
                <th>Firma / Owner</th>
                <th style={{ width: 100 }}>Status</th>
                <th style={{ width: 110 }}>Brand-Tenant</th>
                <th style={{ width: 90 }} className="text-end">Marge</th>
                <th style={{ width: 130 }}>Beantragt</th>
                <th style={{ width: 220 }} className="text-end">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id}>
                  <td>
                    {r.companyName || <span className="text-muted">— ohne Firma —</span>}
                    <div className="small text-muted">{r.ownerEmail}{r.ownerName ? ` · ${r.ownerName}` : ''}</div>
                  </td>
                  <td><Badge color={STATUS_COLOR[r.status] ?? 'secondary'} pill>{STATUS_LABEL[r.status] ?? r.status}</Badge></td>
                  <td className="small">{r.tenantId ? `#${r.tenantId}` : <span className="text-muted">nicht provisioniert</span>}</td>
                  <td className="text-end small">{(Number(r.defaultMarkupBps) / 100).toFixed(1)} %</td>
                  <td><RelativeTime iso={r.requestedAt} /></td>
                  <td className="text-end">
                    {canWrite && r.status === 'pending' && (
                      <Button size="sm" color="success" outline disabled={approve.isPending}
                        onClick={() => approve.mutate({ id: r.id })}>Freigeben</Button>
                    )}
                    {canWrite && r.status === 'active' && (
                      <Button size="sm" color="danger" outline disabled={setStatusM.isPending}
                        onClick={() => suspend(r)}>Sperren</Button>
                    )}
                    {canWrite && r.status === 'suspended' && (
                      <Button size="sm" color="success" outline disabled={setStatusM.isPending}
                        onClick={() => reactivate(r)}>Reaktivieren</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </CardBody>
      </Card>
    </div>
  );
}
